import { useNavigate } from 'react-router-dom';
import { TrendingUp, DollarSign, Network, Target, BarChart3, Shield, ArrowRight, Check, Users, Globe } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

function ForResortsPage() {
  const navigate = useNavigate();

  const benefits = [
    {
      icon: TrendingUp,
      title: 'Direct Bookings Growth',
      description: 'Increase direct bookings by an average of 45% within 6 months through our referral network',
      stats: '45% Average Increase'
    },
    {
      icon: DollarSign,
      title: 'Low Monthly Subscription',
      description: 'Simple, transparent pricing with no per-booking commissions. Pay a flat monthly fee and keep 100% of your revenue',
      stats: 'Zero Commission'
    },
    {
      icon: Network,
      title: 'Cross-Promotion Network',
      description: 'Get featured across 500+ partner resorts and reach thousands of quality travelers every month',
      stats: '500+ Partners'
    },
    {
      icon: Target,
      title: 'Brand Visibility',
      description: 'Showcase your property to the right audience through targeted promotions and partner referrals',
      stats: '10K+ Monthly Views'
    },
    {
      icon: BarChart3,
      title: 'Real-Time Analytics',
      description: 'Track referrals, bookings, revenue, and performance with comprehensive dashboard and reports',
      stats: 'Live Dashboard'
    },
    {
      icon: Shield,
      title: 'Verified Partners Only',
      description: 'All properties are thoroughly verified for quality, ensuring you are part of a trusted network',
      stats: '100% Verified'
    }
  ];

  const features = [
    'Unlimited property listings (based on plan)',
    'Professional profile with photo gallery',
    'Featured placement in newsletters',
    'Priority guest referrals',
    'Advanced analytics dashboard',
    'Dedicated account manager',
    '24/7 priority support',
    'Marketing materials support',
    'Quarterly networking events',
    'Custom landing pages (Enterprise)',
    'API integration (Enterprise)',
    'White-label solutions (Enterprise)'
  ];

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-emerald-600 to-emerald-800 text-white" data-testid="for-resorts-hero">
        <div className="max-w-4xl mx-auto text-center">
          <Badge className="mb-4 bg-white text-emerald-600 px-4 py-2">For Resort Owners</Badge>
          <h1 className="text-5xl md:text-6xl font-bold mb-6">Grow Your Resort Without OTA Commissions</h1>
          <p className="text-xl text-emerald-100 mb-8">
            Join India's largest network of independent resorts. Get more direct bookings through collaboration, not competition.
          </p>
          <Button 
            size="lg" 
            className="bg-white text-emerald-600 hover:bg-gray-100 text-lg px-10 py-6"
            onClick={() => navigate('/join')}
          >
            Join Travelodges Today
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white" data-testid="benefits-section">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Why Resort Owners Choose Us</h2>
            <p className="text-xl text-gray-600">Everything you need to grow your resort business</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => (
              <Card key={index} className="border-2 hover:border-emerald-500 transition-all hover:shadow-xl" data-testid={`benefit-${index}`}>
                <CardContent className="pt-8 pb-8">
                  <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mb-4">
                    <benefit.icon className="h-8 w-8 text-emerald-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{benefit.title}</h3>
                  <p className="text-gray-600 mb-3">{benefit.description}</p>
                  <Badge className="bg-emerald-100 text-emerald-800">{benefit.stats}</Badge>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* How It Helps Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50" data-testid="helps-section">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">Built Specifically for Independent Resorts</h2>
              <p className="text-lg text-gray-600 mb-6">
                Unlike OTAs that take 15-30% commission on every booking, Travelodges works on a simple subscription model. Pay a predictable monthly fee and keep 100% of your booking revenue.
              </p>
              <p className="text-lg text-gray-600 mb-6">
                Our platform is designed to help small and mid-sized resorts compete with large chains by building a powerful referral network where everyone grows together.
              </p>
              <Button 
                size="lg"
                onClick={() => navigate('/how-it-works')}
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                See How It Works
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </div>
            <Card className="bg-gradient-to-br from-emerald-600 to-emerald-700 text-white">
              <CardContent className="p-8">
                <h3 className="text-2xl font-bold mb-6">Compare the Savings</h3>
                <div className="space-y-4">
                  <div className="bg-white/10 rounded-lg p-4">
                    <div className="text-sm text-emerald-100 mb-1">Traditional OTAs</div>
                    <div className="text-3xl font-bold">15-30%</div>
                    <div className="text-sm text-emerald-100">Commission per booking</div>
                  </div>
                  <div className="text-center text-2xl font-bold">vs</div>
                  <div className="bg-white/10 rounded-lg p-4">
                    <div className="text-sm text-emerald-100 mb-1">Travelodges</div>
                    <div className="text-3xl font-bold">₹999-₹4,999</div>
                    <div className="text-sm text-emerald-100">Flat monthly fee</div>
                  </div>
                </div>
                <div className="mt-6 pt-6 border-t border-white/20">
                  <div className="flex items-center space-x-2">
                    <Check className="h-5 w-5" />
                    <span>Save thousands every month</span>
                  </div>
                  <div className="flex items-center space-x-2 mt-2">
                    <Check className="h-5 w-5" />
                    <span>No hidden fees or surprises</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white" data-testid="features-section">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Everything You Need to Succeed</h2>
            <p className="text-xl text-gray-600">Comprehensive features to grow your resort business</p>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            {features.map((feature, index) => (
              <div key={index} className="flex items-start space-x-3 p-4 rounded-lg hover:bg-emerald-50 transition-colors">
                <Check className="h-6 w-6 text-emerald-600 flex-shrink-0 mt-0.5" />
                <span className="text-gray-700 text-lg">{feature}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-emerald-50" data-testid="testimonials-section">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">What Resort Owners Say</h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <Card className="bg-white">
              <CardContent className="pt-6">
                <p className="text-gray-700 italic mb-4">
                  "Since joining Travelodges, our direct bookings increased by 50%. The referral network is incredible!"
                </p>
                <div className="border-t pt-4">
                  <p className="font-semibold">Rajesh Kumar</p>
                  <p className="text-sm text-gray-600">Misty Hills Resort, Coorg</p>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-white">
              <CardContent className="pt-6">
                <p className="text-gray-700 italic mb-4">
                  "Best decision for our business. We now receive consistent quality referrals from partner resorts."
                </p>
                <div className="border-t pt-4">
                  <p className="font-semibold">Priya Sharma</p>
                  <p className="text-sm text-gray-600">Seaside Retreat, Goa</p>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-white">
              <CardContent className="pt-6">
                <p className="text-gray-700 italic mb-4">
                  "Travelodges helped us maintain occupancy during off-season. The platform is a game-changer."
                </p>
                <div className="border-t pt-4">
                  <p className="font-semibold">Amit Patel</p>
                  <p className="text-sm text-gray-600">Wildlife Lodge, Ranthambore</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-emerald-600 to-emerald-800" data-testid="final-cta">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Join 500+ Successful Resort Owners
          </h2>
          <p className="text-xl text-emerald-100 mb-10">
            Start growing your direct bookings today. No credit card required for initial consultation.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg" 
              className="bg-white text-emerald-600 hover:bg-gray-100 text-lg px-10 py-6"
              onClick={() => navigate('/join')}
            >
              List Your Property
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            <Button 
              size="lg" 
              variant="outline"
              className="bg-transparent border-2 border-white text-white hover:bg-white/10 text-lg px-10 py-6"
              onClick={() => navigate('/pricing')}
            >
              View Pricing Plans
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}

export default ForResortsPage;